// CustomDataGrid.js
import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import {
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    GridToolbarQuickFilter,
    GridToolbarExport,
} from '@mui/x-data-grid-premium';

// function CustomToolbar() {
//     return (
//         <GridToolbarContainer>
//             <GridToolbarColumnsButton />
//             <GridToolbarFilterButton />
//             <GridToolbarDensitySelector
//                 slotProps={{ tooltip: { title: 'Change density' } }}
//             />
//             <Box sx={{ flexGrow: 1 }} />
//             {/* <button>Hello</button> */}
//             <GridToolbarExport
//                 slotProps={{
//                     tooltip: { title: 'Export data' },
//                     button: { variant: 'outlined' },
//                 }}
//             />
//         </GridToolbarContainer>
//     );
// }

const escapeHtml = (value) =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const formatDateForPrint = (value) => {
    if (!value) {
        return value;
    }

    const asString = String(value).trim();
    const isoMatch = asString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return `${day}.${month}.${year}`;
    }

    return value;
};

const getTodayFormatted = () => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
};

function DataGridComponent({ columns, rows, height, enablePrint = false, printTitle = 'Raport' }) {
    const handlePrint = React.useCallback(() => {
        if (!rows?.length) {
            return;
        }

        const printableColumns = columns.filter((column) => column?.field);

        const totalColumnWidth = printableColumns.reduce(
            (sum, column) => sum + (Number(column.width) || 140),
            0,
        );

        const colGroupHtml = printableColumns
            .map((column) => {
                const width = Number(column.width) || 140;
                const percent = (width / totalColumnWidth) * 100;
                return `<col style="width:${percent.toFixed(2)}%">`;
            })
            .join('');

        const headerHtml = printableColumns
            .map((column) => `<th>${escapeHtml(column.headerName || column.field)}</th>`)
            .join('');

        const rowsHtml = rows
            .map((row) => {
                const cells = printableColumns
                    .map((column) => {
                        const rawValue = row?.[column.field];
                        const formattedValue =
                            typeof column.valueFormatter === 'function'
                                ? column.valueFormatter(rawValue)
                                : rawValue;
                        const displayValue =
                            String(column.field || '').toLowerCase().includes('date')
                                ? formatDateForPrint(formattedValue)
                                : formattedValue;
                        const isNumericColumn =
                            column.type === 'number'
                            || /^(debit|credit|progressive|total|amount|qty|price|value)/i.test(String(column.field || ''));

                        const tdClass = isNumericColumn ? 'num-cell' : '';

                        return `<td class="${tdClass}">${escapeHtml(displayValue ?? '')}</td>`;
                    })
                    .join('');

                return `<tr>${cells}</tr>`;
            })
            .join('');

        const printFrame = document.createElement('iframe');
        printFrame.style.position = 'fixed';
        printFrame.style.right = '0';
        printFrame.style.bottom = '0';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = '0';
        printFrame.style.visibility = 'hidden';
        document.body.appendChild(printFrame);

        const frameWindow = printFrame.contentWindow;
        const frameDocument = frameWindow?.document;

        if (!frameWindow || !frameDocument) {
            document.body.removeChild(printFrame);
            return;
        }

        frameDocument.open();
        frameDocument.write(`
            <!doctype html>
            <html>
                <head>
                    <title>${escapeHtml(printTitle)}</title>
                    <style>
                        @page {
                            size: A4 landscape;
                            margin: 8mm;
                        }
                        body {
                            font-family: Arial, sans-serif;
                            margin: 0;
                            color: #1f3a36;
                            background: #ffffff;
                        }
                        .print-wrapper {
                            width: 100%;
                        }
                        .report-head {
                            border: 1px solid #c8d8cf;
                            border-left: 5px solid #284945;
                            background: linear-gradient(180deg, #f5f9f7 0%, #ecf4ef 100%);
                            padding: 10px 12px;
                            margin-bottom: 10px;
                        }
                        .report-title {
                            margin: 0;
                            font-size: 18px;
                            color: #1f3a36;
                            font-weight: 700;
                            line-height: 1.2;
                        }
                        .report-meta {
                            margin-top: 6px;
                            display: flex;
                            gap: 12px;
                            flex-wrap: wrap;
                            font-size: 11px;
                            color: #3b6152;
                        }
                        .report-meta-item {
                            padding: 2px 8px;
                            background: #e9f1ed;
                            border: 1px solid #d4e2db;
                            border-radius: 12px;
                        }
                        table {
                            border-collapse: collapse;
                            width: 100%;
                            table-layout: fixed;
                        }
                        th, td {
                            border: 1px solid #c8d8cf;
                            padding: 6px;
                            font-size: 11px;
                            word-break: break-word;
                            text-align: left;
                            vertical-align: top;
                        }
                        th {
                            background: #284945;
                            color: #ffffff;
                            font-weight: 700;
                            white-space: nowrap;
                        }
                        .num-cell {
                            text-align: right;
                            font-variant-numeric: tabular-nums;
                        }
                        tr:nth-child(even) td {
                            background: #f8fbf9;
                        }
                        @media print {
                            @page {
                                size: A4 landscape;
                                margin: 8mm;
                            }
                            html, body {
                                width: 297mm;
                                min-height: 210mm;
                            }
                            thead { display: table-header-group; }
                            tr { page-break-inside: avoid; }
                            table {
                                width: 100%;
                                font-size: 10.5px;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="print-wrapper">
                        <div class="report-head">
                            <h2 class="report-title">${escapeHtml(printTitle)}</h2>
                            <div class="report-meta">
                                <span class="report-meta-item">Data e printimit: ${escapeHtml(getTodayFormatted())}</span>
                                <span class="report-meta-item">Rreshta: ${escapeHtml(rows.length)}</span>
                            </div>
                        </div>
                        <table>
                            <colgroup>${colGroupHtml}</colgroup>
                            <thead>
                                <tr>${headerHtml}</tr>
                            </thead>
                            <tbody>
                                ${rowsHtml}
                            </tbody>
                        </table>
                    </div>
                </body>
            </html>
        `);

        frameDocument.close();

        const triggerPrint = () => {
            frameWindow.focus();
            frameWindow.print();

            window.setTimeout(() => {
                if (document.body.contains(printFrame)) {
                    document.body.removeChild(printFrame);
                }
            }, 500);
        };

        if (frameDocument.readyState === 'complete') {
            window.setTimeout(triggerPrint, 50);
        } else {
            printFrame.onload = () => {
                window.setTimeout(triggerPrint, 50);
            };
        }
    }, [columns, rows, printTitle]);

    const Toolbar = React.useCallback(
        () => (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarDensitySelector />
                <GridToolbarExport />
                {enablePrint && (
                    <Button
                        size="small"
                        variant="text"
                        onClick={handlePrint}
                        disabled={!rows?.length}
                        sx={{ ml: 1 }}
                    >
                        Print
                    </Button>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <GridToolbarQuickFilter />
            </GridToolbarContainer>
        ),
        [enablePrint, handlePrint, rows],
    );

    return (
        <>
            <Box
                sx={{
                    height: height ? height : 500,
                    width: '100%',
                    border: "solid lightgray 1px",
                    '& .super-app-theme--header': {
                        backgroundColor: '#284945', color: "white"
                    },
                }}
            >
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={15}
                    // disableRowSelectionOnClick
                    getRowClassName={(params) => params.row.className}
                    initialState={{
                        density: 'compact'
                    }}
                    slots={{
                        toolbar: Toolbar,
                    }}
                />
                <style jsx>{`
                            .zero-balance-row {
                                background-color: lightgray;
                            }
                            .non-zero-balance-row {
                                /* Stili për rreshtat kur 'balance' nuk është 0 */
                            }
                `}</style>
            </Box>

        </>

    );
}

export default DataGridComponent;
